import { useCallback, useMemo } from 'react';

import store, {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from '@store';

export const useCollectionFilters = () => {
  const collection = useRecoilValue(store.collection);
  const [selectedFilters, setSelectedFilters] = useRecoilState(
    store.selectedFilters
  );
  const setCurrentResultsPage = useSetRecoilState(store.currentResultsPage);

  const selectedFiltersDep = JSON.stringify(selectedFilters);

  const totalResults = collection?.pagination?.totalResults || 0;

  const filters = useMemo(() => {
    if (!totalResults || !collection?.facets?.length) return null;

    return collection.facets.filter((facet) => {
      if (facet.values.length === 1 && facet.values[0].count === totalResults) {
        return false;
      }
      return true;
    });
  }, [collection?.facets, totalResults]);

  // get filter entries based on type
  const getFilterEntries = useCallback(({ field, option }) => {
    let _filters = {};

    // if option type is range, separate into low and high entries
    if (option.type === 'range') {
      const [key1, value1] = [`filter.${field}.low`, option.low];
      const [key2, value2] = [`filter.${field}.high`, option.high];
      _filters = {
        [`${key1}.${value1}`]: { key: key1, value: value1 },
        [`${key2}.${value2}`]: { key: key2, value: value2 },
      };
    } else {
      const [key, value] = [`filter.${field}`, option.value || ''];
      _filters = {
        [`${key}.${value}`]: { key, value },
      };
    }

    return _filters;
  }, []);

  // add to filters using filterOptions
  const addToSelectedFilters = useCallback(
    ({ field, option }) => {
      if (!field || !option) {
        return console.error('addToSelectedFilters: missing field or option');
      }
      // Example ss field: 'product_type'
      // Example ss option: { active: false, type: 'value', value: 'Shirts' ... }

      // create map of previous selected filters
      const selectedFiltersMap = selectedFilters?.reduce((obj, filter) => {
        obj[`${filter.key}.${filter.value}`] = filter;
        return obj;
      }, {});

      // prevent duplicates by spreading previous and new filters into new map
      const newSelectedFiltersMap = {
        ...selectedFiltersMap,
        ...getFilterEntries({ field, option }),
      };

      // convert filters map back into array
      const newSelectedFilters = Object.values(newSelectedFiltersMap);

      setSelectedFilters(newSelectedFilters);
      setCurrentResultsPage(1);
    },
    [selectedFiltersDep]
  );

  // remove from selected filters
  const removeFromSelectedFilters = useCallback(
    ({ field, option }) => {
      if (!field || !option) {
        return console.error(
          'removeFromSelectedFilters: missing field or option'
        );
      }
      // Example ss field / option: 'collection_name', { value: 'Shirts', type: 'value }
      // Example ss field / option w/ range: 'ss_price', { high: '80', low: '60', type: 'range }

      if (!selectedFilters?.length) return;

      // filter out filters that match the field and option
      const newSelectedFilters = selectedFilters.filter((filter) => {
        if (option.type === 'range') {
          const isEntryLow =
            filter.key === `filter.${field}.low` && filter.value === option.low;

          const isEntryHigh =
            filter.key === `filter.${field}.high` &&
            filter.value === option.high;

          return !isEntryLow && !isEntryHigh;
        }
        const isEntry =
          filter.key === `filter.${field}` && filter.value === option.value;

        return !isEntry;
      });

      setSelectedFilters(newSelectedFilters);
      setCurrentResultsPage(1);
    },
    [selectedFiltersDep]
  );

  // clear filters
  const clearSelectedFilters = useCallback(() => {
    setSelectedFilters(null);
    setCurrentResultsPage(1);
  }, []);

  return [
    // state
    {
      selectedFilters,
      filters,
      filterSummary: collection?.filterSummary || null,
      totalResults: totalResults - (collection?.merchandising?.content?.inline?.length || 0),
    },
    // actions
    {
      addToSelectedFilters,
      removeFromSelectedFilters,
      clearSelectedFilters,
    },
  ];
};
